import { isAssessmentDomain } from '@/services/domain/recordingContract';
import { numericTotalFields } from '@/services/domain/numericTotal';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  AcademicCapIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  HandRaisedIcon,
  HeartIcon,
  LockClosedIcon,
  PlusIcon,
  RectangleStackIcon,
  ShieldCheckIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { useSchool } from '@/contexts/SchoolContext';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useRoleSwitcher } from '@/features/auth/hooks/useRoleSwitcher';
import { useDomainStore } from '@/stores/domainStore';
import DomainRegistryNotice from '@/features/domains/components/DomainRegistryNotice';
import { ROUTES } from '@/utils/constants';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ExpandableCard from '@/components/shared/ExpandableCard';
import { DomainSettingsCard } from '@/features/domains/components/DomainSettingsCard';
import { DomainCreationForm } from '@/features/domains/components/DomainCreationForm';
import DomainActionDefinitionsManager from '@/features/domains/components/DomainActionDefinitionsManager';
import { DomainActionsManager } from '@/features/domains/components/DomainActionsManager';
import DisciplineRules from '@/features/discipline/components/DisciplineRules';
import OffencesManager from '@/features/discipline/components/OffencesManager';
import ConsequenceTypesManager from '@/features/discipline/components/ConsequenceTypesManager';
import ValuesManager from '@/features/discipline/components/ValuesManager';
import DisciplineBriefSettings from '@/features/discipline/components/DisciplineBriefSettings';
import WeeklySummarySettings from '@/features/admin/components/WeeklySummarySettings';
import FieldsEditor from '@/features/domains/components/FieldsEditor';
import PhaseScopeSelector from '@/components/ui/PhaseScopeSelector';
import usePhaseConfigurationView from '@/features/phases/usePhaseConfigurationView';
import { setDomainCollectiveReporting, updateDomain } from '@/services/domainRegistry';
import {
  reportingFields,
  reportingProfile,
} from '@/services/domain/domainStatsService';
import {
  buildDomainCapabilities,
  buildDomainSurfaces,
  canConfigureDomainCapability,
  capabilityIsEnabled,
  domainSupportsSurface,
  getDomainConfigurationPolicy,
} from '@/services/domain/centralModel';

const StickersManager = lazy(() => import('@/features/stickers/components/StickersManager'));
const AwardsManager = lazy(() => import('@/features/awards/components/AwardsManager'));

const REMEMBERED_DOMAIN_KEY_PREFIX = 'swan_domains_selected_';
const LEGACY_TABS = Object.freeze({
  setup: 'domain',
  categories: 'event',
  events: 'event',
  consequences: 'action',
  actions: 'action',
});
const TABS = [
  { key: 'domain', label: 'Domain' },
  { key: 'event', label: 'Event' },
  { key: 'action', label: 'Action' },
  { key: 'rules', label: 'Rules' },
  { key: 'reports', label: 'Reports' },
];

const isRootDomain = (domain) => !domain.parentDomainId;
const displayLabel = (domain) => (domain?.id === 'discipline' ? 'Behaviour' : domain?.label);

const DOMAIN_HEADER_ICON_RULES = [
  { matches: ['discipline', 'behaviour'], Icon: ShieldCheckIcon },
  { matches: ['recognition'], Icon: StarIcon },
  { matches: ['attendance', 'daily-register', 'period-register'], Icon: CalendarDaysIcon },
  { matches: ['participation'], Icon: HandRaisedIcon },
  { matches: ['health'], Icon: HeartIcon },
  { matches: ['academics', 'marks'], Icon: AcademicCapIcon },
  { matches: ['policy', 'policies'], Icon: DocumentTextIcon },
  { matches: ['incident'], Icon: ClipboardDocumentCheckIcon },
  { matches: ['homework'], Icon: BookOpenIcon },
  { matches: ['consent'], Icon: ClipboardDocumentCheckIcon },
];

function domainHeaderIcon(domain) {
  const identity = `${domain?.id || ''} ${domain?.label || ''}`.toLowerCase();
  return DOMAIN_HEADER_ICON_RULES.find(({ matches }) => (
    matches.some((match) => identity.includes(match))
  ))?.Icon || RectangleStackIcon;
}

function activeDefinitions(items) {
  return (Array.isArray(items) ? items : []).filter((item) => (
    item && item.active !== false && item.retired !== true
  ));
}

function domainHeaderFacts(domain) {
  const policy = getDomainConfigurationPolicy(domain);
  const eventAdapter = policy?.event?.adapter;
  const actionAdapter = policy?.actions?.adapter;
  const fields = activeDefinitions([domain?.primary, ...(domain?.followUp || [])]);
  const actions = activeDefinitions(domain?.actionDefinitions);
  const mechanisms = activeDefinitions(
    policy?.rules?.mechanisms
      || domain?.capabilities?.rules?.mechanisms
      || domain?.capabilities?.rules?.categories,
  );

  const eventFact = eventAdapter && eventAdapter !== 'canonical-fields'
    ? { value: 'Swan', label: 'Event editor' }
    : { value: fields.length || '—', label: fields.length === 1 ? 'Event field' : 'Event fields' };

  let actionFact = { value: '—', label: 'Actions' };
  if (actions.length) {
    actionFact = { value: actions.length, label: actions.length === 1 ? 'Action' : 'Actions' };
  } else if (actionAdapter && actionAdapter !== 'action-definitions') {
    actionFact = { value: 'Swan', label: 'Action editor' };
  } else if ((policy?.actions?.outcomes || []).length) {
    actionFact = { value: 'Ready', label: 'Actions' };
  }

  const rulesEnabled = capabilityIsEnabled(domain, 'rules');
  const ruleFact = mechanisms.length
    ? { value: mechanisms.length, label: mechanisms.length === 1 ? 'Rule type' : 'Rule types' }
    : rulesEnabled
      ? { value: 'On', label: 'Rules' }
      : { value: '—', label: 'Rules' };

  return [eventFact, actionFact, ruleFact];
}

function domainGovernanceSummary(domain) {
  return domain?.governance?.definitionAuthority === 'school'
    ? 'School Domain · governed by Swan model'
    : 'Swan Domain Standard · configured for this school';
}

function orderedDomainEntries(domains) {
  const sorted = [...domains].sort((left, right) => (
    (left.sortOrder || 0) - (right.sortOrder || 0)
      || displayLabel(left).localeCompare(displayLabel(right))
  ));
  const byParent = new Map();
  sorted.forEach((domain) => {
    const parentId = domain.parentDomainId || null;
    byParent.set(parentId, [...(byParent.get(parentId) || []), domain]);
  });
  const output = [];
  const visited = new Set();
  const visit = (domain, depth) => {
    if (visited.has(domain.id)) return;
    visited.add(domain.id);
    output.push({ domain, depth });
    (byParent.get(domain.id) || []).forEach((child) => visit(child, depth + 1));
  };
  (byParent.get(null) || []).forEach((domain) => visit(domain, 0));
  sorted.forEach((domain) => visit(domain, domain.parentDomainId ? 1 : 0));
  return output;
}

function ReportPreview({ domain }) {
  const fields = reportingFields(domain).filter((field) => field && field.retired !== true);

  const descriptionFor = (field) => {
    const type = field.type === 'binary' ? 'boolean' : field.type;
    if (type === 'multiChoice') {
      const choices = (field.options || [])
        .filter((option) => option?.retired !== true && option?.active !== false)
        .map((option) => option.label || option.value)
        .filter(Boolean);
      return `${choices.length ? choices.join(', ') : 'Each choice'} — totals, percentages, breakdown and trend`;
    }
    if (type === 'boolean') return 'Yes and No — totals, percentages, breakdown and trend';
    if (type === 'number') {
      return `Entries, scores${field.maxScore ? ` out of ${field.maxScore}` : ' with recorded maximums'}, averages and trends`;
    }
    if (['string', 'text', 'storageRef'].includes(type)) return 'Completion totals and trends over time';
    if (type === 'rating') return 'Recorded rating values and trends';
    return 'Recorded entries and trends';
  };

  return (
    <section className="min-w-0 overflow-hidden rounded-xl border border-base-300 bg-base-100 p-4">
      <h3 className="font-semibold">Report preview</h3>
      <p className="mt-1 text-sm text-base-content/60">Measures follow the answers configured under Event.</p>
      {fields.length ? <dl className="mt-3 divide-y divide-base-200 rounded-lg border border-base-200">
        {fields.map((field) => (
          <div key={field.id || field.label} className="grid gap-1 px-3 py-2.5 sm:grid-cols-[minmax(8rem,0.35fr)_1fr] sm:gap-4">
            <dt className="text-sm font-medium">{field.label || 'Event'}</dt>
            <dd className="min-w-0 break-words text-sm text-base-content/60">{descriptionFor(field)}</dd>
          </div>
        ))}
        {numericTotalFields(domain).length > 1 && (
          <div className="grid gap-1 px-3 py-2.5 sm:grid-cols-[minmax(8rem,0.35fr)_1fr] sm:gap-4">
            <dt className="text-sm font-medium">Across number fields</dt>
            <dd className="text-sm text-base-content/60">Combined percentages use the saved contributions and maxima.</dd>
          </div>
        )}
      </dl> : (
        <p className="mt-3 rounded-lg border border-dashed border-base-300 p-4 text-sm text-base-content/55">
          Add an Event field before choosing report measures.
        </p>
      )}
    </section>
  );
}

function sentenceList(values) {
  if (values.length === 0) return '';
  if (values.length === 1) return values[0];
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')} and ${values.at(-1)}`;
}

function reportMeasureDescription(domain) {
  return sentenceList(reportingProfile(domain).map(({ label }) => (
    label.charAt(0).toLowerCase() + label.slice(1)
  )));
}

function buildSearch(searchParams, patch) {
  const next = new URLSearchParams(searchParams);
  Object.entries(patch).forEach(([key, value]) => {
    if (value === null || value === undefined || value === false || value === '') next.delete(key);
    else next.set(key, String(value));
  });
  return next;
}

function EmptyState({ title, children, actionLabel, onAction, actionTo }) {
  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-dashed border-base-300 px-5 py-8 text-center">
      {title && <p className="font-medium">{title}</p>}
      <p className={`${title ? 'mt-1 ' : ''}text-sm text-base-content/55`}>{children}</p>
      {actionLabel && (actionTo ? (
        <Link className="btn btn-outline btn-sm mt-4" to={actionTo}>{actionLabel}</Link>
      ) : (
        <button type="button" className="btn btn-outline btn-sm mt-4" onClick={onAction}>
          {actionLabel}
        </button>
      ))}
    </div>
  );
}

function ManagedCapability({ title, children, actionLabel, actionTo, onAction }) {
  return (
    <section className="rounded-xl border border-base-300 bg-base-100 p-5">
      <div className="max-w-2xl">
        <h3 className="font-semibold">{title}</h3>
        <p className="mt-1 text-sm leading-relaxed text-base-content/60">{children}</p>
        {actionLabel && (actionTo ? (
          <Link className="btn btn-outline btn-sm mt-4" to={actionTo}>{actionLabel}</Link>
        ) : (
          <button type="button" className="btn btn-outline btn-sm mt-4" onClick={onAction}>
            {actionLabel}
          </button>
        ))}
      </div>
    </section>
  );
}

function SaveBadge({ status, error }) {
  if (status === 'saving') return <span className="badge badge-info badge-outline">Saving…</span>;
  if (status === 'saved') {
    return (
      <span className="badge badge-success badge-outline gap-1">
        <CheckCircleIcon className="h-3.5 w-3.5" /> Saved
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="badge badge-error badge-outline gap-1" title={error || undefined}>
        <ExclamationCircleIcon className="h-3.5 w-3.5" /> Save failed
      </span>
    );
  }
  return null;
}

function DomainsQuickPanel() {
  const { domains, activeDomain, isLoaded, registryError } = useDomainStore();
  if (!isLoaded) return registryError ? <DomainRegistryNotice /> : <div className="flex justify-center py-8"><LoadingSpinner /></div>;
  const roots = domains.filter(isRootDomain)
    .sort((left, right) => (left.sortOrder || 0) - (right.sortOrder || 0));

  return (
    <div className="space-y-4 p-4 pb-24">
      <DomainRegistryNotice />
      <div>
        <h3 className="text-sm font-semibold">Domains</h3>
        <p className="mt-1 text-xs text-base-content/60">
          Configure what your school records and what Swan does with it.
        </p>
      </div>
      <Link to={`${ROUTES.DOMAINS}?create=1`} className="btn btn-primary btn-sm w-full gap-1">
        <PlusIcon className="h-4 w-4" /> New Domain
      </Link>
      {activeDomain && (
        <Link
          to={`${ROUTES.DOMAINS}?domain=${encodeURIComponent(activeDomain.id)}&step=domain`}
          className="flex items-center gap-2 rounded-xl border border-primary/25 bg-primary/5 p-3 text-sm"
        >
          <CheckCircleIcon className="h-4 w-4 shrink-0 text-primary" />
          <span className="min-w-0 flex-1">
            <span className="block text-xs text-base-content/55">In use now</span>
            <span className="block truncate font-medium">{displayLabel(activeDomain)}</span>
          </span>
          <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0" />
        </Link>
      )}
      <nav className="space-y-2" aria-label="Domains">
        {roots.map((domain) => (
          <Link
            key={domain.id}
            to={`${ROUTES.DOMAINS}?domain=${encodeURIComponent(domain.id)}&step=domain`}
            className="flex items-center gap-3 rounded-xl border border-base-300 bg-base-100 p-3 transition hover:border-primary/40 hover:bg-base-200/40"
          >
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{displayLabel(domain)}</span>
            {domain.active === false && <span className="badge badge-ghost badge-sm">Inactive</span>}
            <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0 text-base-content/40" />
          </Link>
        ))}
      </nav>
    </div>
  );
}

function EventTab({ domain, onNext }) {
  if (isAssessmentDomain(domain)) return <div className="space-y-4">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div><h3 className="font-semibold">Assessment result</h3>
        <p className="mt-1 max-w-xl text-sm text-base-content/60">The selected assessment defines Points, Levels and result statuses. Add other questions below; staff answer them in each learner’s details.</p>
        <p className="mt-1 text-xs text-base-content/60">Changes apply to new mark sheets. Saved sheets and published results keep their original questions.</p>
      </div>
      <Link className="btn btn-outline btn-sm" to="/setup?area=academics&tab=programmes">Programme setup</Link>
    </div>
    <FieldsEditor domain={domain} supplementaryOnly />
  </div>;
  const policy = getDomainConfigurationPolicy(domain);
  const adapter = policy?.event?.adapter;
  if (adapter === 'behaviour-events') {
    return (
      <div className="space-y-8">
        <ConsequenceTypesManager embedded domain={domain} mode="mention" visibleSections="types" />
        <section className="border-t border-base-200 pt-7">
          <OffencesManager embedded domain={domain} />
        </section>
      </div>
    );
  }
  if (adapter === 'recognition-events') {
    return (
      <div className="space-y-8">
        <ConsequenceTypesManager embedded domain={domain} mode="recognition" visibleSections="types" />
        <section className="border-t border-base-200 pt-7">
          <OffencesManager embedded domain={domain} />
        </section>
        <section className="border-t border-base-200 pt-7">
          <ValuesManager embedded domain={domain} />
        </section>
      </div>
    );
  }
  if (!capabilityIsEnabled(domain, 'recording')) {
    const route = domain.surfaces?.standalone?.route;
    return (
      <ManagedCapability
        title="This Domain does not record student events"
        actionLabel={route ? `Open ${displayLabel(domain)}` : 'Continue to Action'}
        actionTo={route || undefined}
        onAction={route ? undefined : onNext}
      >
        Its content is managed in its established workspace, so Swan does not invent fields or recording forms here.
      </ManagedCapability>
    );
  }
  if (adapter !== 'canonical-fields') {
    return (
      <ManagedCapability title="This Event editor is managed by its Domain Standard" actionLabel="Continue to Action" onAction={onNext}>
        Swan keeps the established recording structure intact for this Domain.
      </ManagedCapability>
    );
  }
  return <FieldsEditor domain={domain} />;
}

function ActionTab({
  domain,
  schoolId,
  onPrevious,
  onNext,
  awardEditorRequest,
  onAwardEditorClose,
}) {
  const policy = getDomainConfigurationPolicy(domain);
  const adapter = policy?.actions?.adapter;
  const outcomes = policy?.actions?.outcomes || [];
  const messageDelivery = domain.capabilities?.messages?.delivery;
  const actionsSupported = policy?.capabilities?.actions !== 'unsupported'
    && policy?.actions?.operations?.length > 0;

  if (adapter === 'behaviour-actions') {
    return <ConsequenceTypesManager embedded domain={domain} mode="mention" visibleSections="actions" />;
  }
  if (adapter === 'recognition-actions') {
    return (
      <div className="space-y-8">
        <ConsequenceTypesManager embedded domain={domain} mode="recognition" visibleSections="actions" />
        <Suspense fallback={<LoadingSpinner />}>
          <section className="border-t border-base-200 pt-7"><StickersManager embedded /></section>
          <section className="border-t border-base-200 pt-7">
            <AwardsManager
              embedded
              editorRequest={awardEditorRequest}
              onEditorRequestClose={onAwardEditorClose}
            />
          </section>
        </Suspense>
      </div>
    );
  }

  if (outcomes.includes('parent-message-proposal')) {
    return (
      <ManagedCapability title="Reviewed parent follow-up" actionLabel="Open Quick Issue" actionTo={ROUTES.QUICK_ISSUE}>
        Attendance uses Swan’s established parent loop. An eligible attendance event creates a proposed message for staff to review; it is never sent automatically and it never becomes generic Queue work.
      </ManagedCapability>
    );
  }

  if (outcomes.includes('case-follow-up')) {
    return (
      <ManagedCapability title="Case follow-ups use the Incident Reports workflow" actionLabel="Continue to Rules" onAction={onNext}>
        The Domain Standard owns this sensitive case workflow. Assignment and resolution happen in its established operational surface, not in the generic Queue follow-up editor.
      </ManagedCapability>
    );
  }

  if (!actionsSupported || !outcomes.includes('queue-follow-up')) {
    const recordsEvents = capabilityIsEnabled(domain, 'recording');
    const route = domain.surfaces?.standalone?.route;
    return (
      <EmptyState
        title="No Actions for this Domain"
        actionLabel={!recordsEvents && route ? `Open ${displayLabel(domain)}` : 'Review Event'}
        actionTo={!recordsEvents ? route : undefined}
        onAction={recordsEvents ? onPrevious : undefined}
      >
        {recordsEvents
          ? 'This Domain Standard records information without creating follow-up work. Continue using Event to configure what staff record.'
          : 'This Domain Standard uses its established workspace and does not create student Actions or Queue work.'}
      </EmptyState>
    );
  }

  const automaticMessagesSupported = policy?.capabilities?.messages !== 'unsupported'
    && messageDelivery !== 'staff-reviewed-proposal';
  return (
    <div className="space-y-8">
      <DomainActionDefinitionsManager key={`${schoolId}:${domain.id}`} domain={domain} schoolId={schoolId} />
      {automaticMessagesSupported && (
        <section className="border-t border-base-200 pt-7">
          <DomainActionsManager domain={domain} schoolId={schoolId} />
        </section>
      )}
    </div>
  );
}

function CapabilitySwitch({ domain, schoolId, kind }) {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const enabled = kind === 'rules'
    ? capabilityIsEnabled(domain, 'rules') && domainSupportsSurface(domain, 'rules')
    : capabilityIsEnabled(domain, 'collective') && domainSupportsSurface(domain, 'reports');
  const inlineScoreFollowUp = (domain.capabilities?.rules?.mechanisms || domain.capabilities?.rules?.categories || []).includes('score-aggregation')
    && domain.capabilities?.actions?.outcomes?.includes('follow-up');
  const readyForRules = capabilityIsEnabled(domain, 'actions')
    && (inlineScoreFollowUp || (domain.actionDefinitions || []).some((definition) => (
      definition.active !== false && definition.retired !== true
    )));
  const reportMeasures = reportingProfile(domain);
  const policy = getDomainConfigurationPolicy(domain);
  const capability = kind === 'rules' ? 'rules' : 'collective';
  const configurable = canConfigureDomainCapability(domain, capability);
  const ruleMechanisms = policy?.rules?.mechanisms || [];
  const currentRuleDeclaration = domain.capabilities?.rules || {};
  const currentCollectiveDeclaration = domain.capabilities?.collective || {};
  const reportMeasureLabels = reportMeasures.map((measure) => measure.label).join(', ');

  const setEnabled = async (nextEnabled) => {
    setStatus('saving');
    setError(null);
    try {
      if (kind === 'rules') {
        await updateDomain(schoolId, domain.id, {
          capabilities: buildDomainCapabilities({
            ...(domain.capabilities || {}),
            rules: {
              ...currentRuleDeclaration,
              enabled: nextEnabled,
              mechanisms: ruleMechanisms,
              categories: ruleMechanisms,
            },
          }),
          surfaces: buildDomainSurfaces({
            ...(domain.surfaces || {}),
            rules: { ...(domain.surfaces?.rules || {}), enabled: nextEnabled },
          }),
        });
      } else {
        await updateDomain(schoolId, domain.id, {
          capabilities: buildDomainCapabilities({
            ...(domain.capabilities || {}),
            collective: {
              ...currentCollectiveDeclaration,
              enabled: nextEnabled,
              aggregates: currentCollectiveDeclaration.aggregates?.length
                ? currentCollectiveDeclaration.aggregates
                : reportMeasures.map((measure) => measure.key),
              audiences: currentCollectiveDeclaration.audiences || [],
            },
          }),
          surfaces: buildDomainSurfaces({
            ...(domain.surfaces || {}),
            dashboard: { ...(domain.surfaces?.dashboard || {}), enabled: nextEnabled },
            reports: { ...(domain.surfaces?.reports || {}), enabled: nextEnabled },
          }),
        });
      }
      setStatus('saved');
      window.setTimeout(() => setStatus('idle'), 2200);
    } catch (saveError) {
      setError(saveError.message || 'Could not save this setting');
      setStatus('error');
    }
  };

  return (
    <section className="rounded-xl border border-base-300 bg-base-100 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold">{kind === 'rules' ? 'Automated rules' : 'Collective reporting'}</h3>
            <SaveBadge status={status} error={error} />
          </div>
          <p className="mt-1 text-sm text-base-content/60">
            {kind === 'rules'
              ? ruleMechanisms.includes('score-aggregation')
                ? 'Score aggregation rules calculate established academic totals from recorded number fields.'
                : 'Event-response, Action threshold and escalation rules create governed Queue follow-ups.'
              : reportMeasureLabels
                ? `Available measures: ${reportMeasureLabels}.`
                : 'Add an Event field before this Domain can contribute report measures.'}
          </p>
          {kind === 'rules' && !readyForRules && (
            <p className="mt-2 text-xs font-medium text-warning">
              Add an available Queue follow-up under Action before turning rules on.
            </p>
          )}
        </div>
        {configurable ? (
          <label className="flex shrink-0 cursor-pointer items-center gap-2 text-sm font-medium">
            <span>{enabled ? 'Enabled' : 'Disabled'}</span>
            <input
              type="checkbox"
              className="toggle toggle-primary toggle-sm"
              checked={enabled}
              disabled={status === 'saving' || (kind === 'rules' && !enabled && !readyForRules)}
              onChange={(event) => setEnabled(event.target.checked)}
              aria-label={(kind === 'rules' ? 'Enable rules for ' : 'Enable reports for ') + domain.label}
            />
          </label>
        ) : (
          <span className="badge badge-outline shrink-0">{enabled ? 'Swan default · Enabled' : 'Unavailable'}</span>
        )}
      </div>
      {status === 'error' && (
        <div className="alert alert-error mt-3 py-2 text-sm" role="alert">
          <ExclamationCircleIcon className="h-5 w-5" /><span>{error}</span>
        </div>
      )}
    </section>
  );
}

function RulesTab({ domain, schoolId, onRequestAction }) {
  const policy = getDomainConfigurationPolicy(domain);
  const adapter = policy?.rules?.adapter;
  const mechanisms = policy?.rules?.mechanisms || [];
  const supportedMechanisms = mechanisms.filter((mechanism) => (
    ['mention', 'response', 'threshold', 'escalation', 'score-aggregation'].includes(mechanism)
  ));

  if (adapter === 'behaviour-rules' || adapter === 'recognition-rules') {
    return <DisciplineRules embedded domain={domain} />;
  }
  if (mechanisms.includes('parent-loop')) {
    return (
      <ManagedCapability title="Attendance parent loop" actionLabel="Open Quick Issue" actionTo={ROUTES.QUICK_ISSUE}>
        Swan evaluates the established attendance condition after staff record an event, then creates one parent message proposal for review. This rule cannot be replaced with a generic threshold or escalation.
      </ManagedCapability>
    );
  }
  if (policy?.capabilities?.rules === 'unsupported' || supportedMechanisms.length === 0) {
    return (
      <EmptyState title="No automated Rules for this Domain" actionLabel="Review Action" onAction={onRequestAction}>
        This Domain Standard has no supported rule mechanism. Event recording and any established operational workflow continue unchanged.
      </EmptyState>
    );
  }
  const enabled = capabilityIsEnabled(domain, 'rules') && domainSupportsSurface(domain, 'rules');
  return (
    <div className="space-y-5">
      <CapabilitySwitch domain={domain} schoolId={schoolId} kind="rules" />
      {enabled
        ? <DisciplineRules embedded domain={domain} onRequestActionDefinition={onRequestAction} />
        : (
          <EmptyState title="No automated rules yet" actionLabel="Configure Actions" onAction={onRequestAction}>
            Define a reusable follow-up under Action, then enable the rule mechanisms declared by this Domain Standard.
          </EmptyState>
        )}
    </div>
  );
}

function ReportsTab({ domain, schoolId, onPrevious }) {
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const policy = getDomainConfigurationPolicy(domain);
  if (policy?.reports?.adapter === 'behaviour-reports') return <DisciplineBriefSettings embedded />;
  if (policy?.reports?.adapter === 'recognition-reports') {
    return <WeeklySummarySettings embedded context="recognition" />;
  }
  if (policy?.capabilities?.collective === 'unsupported' || policy?.reports?.operations?.length === 0) {
    const route = domain.surfaces?.standalone?.route;
    return (
      <EmptyState
        title="Collective reports are not available for this Domain"
        actionLabel={route ? `Open ${displayLabel(domain)}` : 'Review Rules'}
        actionTo={route || undefined}
        onAction={route ? undefined : onPrevious}
      >
        Swan does not calculate totals or trends for this Domain Standard, so no misleading report measures are shown.
      </EmptyState>
    );
  }

  const enabled = capabilityIsEnabled(domain, 'collective') && domainSupportsSurface(domain, 'reports');
  const audiences = Array.isArray(domain.capabilities?.collective?.audiences)
    ? domain.capabilities.collective.audiences
    : [];
  const measureDescription = reportMeasureDescription(domain);
  const options = [
    ...(audiences.includes('parent-summary') ? [{
      key: 'parentWeeklySummary',
      label: 'Parent weekly summary',
      help: measureDescription
        ? `Include each linked learner’s ${measureDescription} in the existing parent summary.`
        : 'Add an Event field before including this Domain in the existing parent summary.',
    }] : []),
    ...(audiences.includes('staff') ? [{
      key: 'staffWeeklyBrief',
      label: 'Staff weekly brief',
      help: measureDescription
        ? `Include ${measureDescription} in the existing staff brief.`
        : 'Add an Event field before including this Domain in the existing staff brief.',
    }] : []),
  ];

  const setReporting = async (key, checked) => {
    setSaving(key);
    setSaved(false);
    setError(null);
    try {
      await setDomainCollectiveReporting(schoolId, domain.id, key, checked);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2200);
    } catch (saveError) {
      setError(saveError.message || 'Could not save reporting');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-5">
      <CapabilitySwitch domain={domain} schoolId={schoolId} kind="reports" />
      <ReportPreview domain={domain} />
      {enabled ? (
        <section className="space-y-3 rounded-xl border border-base-300 bg-base-100 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold">Summary audiences</h3>
            <SaveBadge status={saving ? 'saving' : error ? 'error' : saved ? 'saved' : 'idle'} error={error} />
          </div>
          {error && <div className="alert alert-error py-2 text-sm">{error}</div>}
          {options.length ? options.map((option) => (
            <label key={option.key} className="flex cursor-pointer items-start gap-3 rounded-lg bg-base-200/50 p-3">
              <input
                type="checkbox"
                className="checkbox checkbox-primary checkbox-sm mt-0.5"
                checked={domain.reporting?.[option.key] === true}
                disabled={saving !== null}
                onChange={(event) => setReporting(option.key, event.target.checked)}
              />
              <span>
                <span className="block text-sm font-medium">{option.label}</span>
                <span className="mt-0.5 block text-xs text-base-content/60">{option.help}</span>
              </span>
            </label>
          )) : (
            <p className="text-sm text-base-content/55">Reporting is enabled with no additional audience choices.</p>
          )}
        </section>
      ) : (
        <EmptyState title="Recording only" actionLabel="Review Rules" onAction={onPrevious}>
          {measureDescription
            ? `Events are saved in Records. Turn on Collective reporting above to make ${measureDescription} available in summaries and reports.`
            : 'Events are saved in Records. Add an Event field before this Domain can contribute report measures.'}
        </EmptyState>
      )}
    </div>
  );
}

function DomainTabs({ activeTab, domain, onSelect }) {
  return (
    <div className="min-w-0 max-w-full overflow-x-auto border-b border-base-300" role="tablist" aria-label={`${displayLabel(domain)} configuration`}>
      <div className="flex w-max min-w-full gap-0.5 px-2 sm:gap-1 sm:px-5">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`border-b-2 px-2 py-2.5 text-xs font-medium transition sm:px-4 sm:py-3 sm:text-sm ${
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-base-content/55 hover:text-base-content'
            }`}
            onClick={() => onSelect(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function DomainJourneyNavigation({ activeTab, domain, onSelect }) {
  const index = TABS.findIndex((tab) => tab.key === activeTab);
  const previous = index > 0 ? TABS[index - 1] : null;
  const next = index >= 0 && index < TABS.length - 1 ? TABS[index + 1] : null;

  return (
    <nav
      className="mt-6 flex items-center justify-between gap-3 border-t border-base-300 pt-4"
      aria-label={`${displayLabel(domain)} configuration journey`}
    >
      <div>
        {previous && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => onSelect(previous.key)}
          >
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
            Back: {previous.label}
          </button>
        )}
      </div>
      <div>
        {next && (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => onSelect(next.key)}
          >
            Next: {next.label}
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </nav>
  );
}

function DomainCardHeader({ domain, isCurrent, expanded }) {
  const Icon = domainHeaderIcon(domain);
  const facts = domainHeaderFacts(domain);
  return (
    <div className={`flex min-w-0 flex-1 items-center gap-3 sm:gap-4 ${expanded ? 'py-1' : ''}`}>
      <span
        className={`flex shrink-0 items-center justify-center rounded-xl ${
          expanded
            ? 'h-12 w-12 bg-primary text-primary-content shadow-sm'
            : 'h-10 w-10 border border-base-300 bg-base-200/45 text-base-content/65'
        }`}
        aria-hidden="true"
      >
        <Icon className={expanded ? 'h-6 w-6' : 'h-5 w-5'} />
      </span>

      <span className="min-w-0 flex-1">
        <span className={`block truncate font-semibold leading-tight ${expanded ? 'text-lg' : 'text-base'}`}>
          {displayLabel(domain)}
        </span>
        <span className="mt-0.5 block truncate text-xs text-base-content/55 sm:text-sm">
          {domain.description || 'School activity Domain'}
        </span>
        {expanded && (
          <span className="mt-1.5 block truncate text-xs text-base-content/45">
            {domainGovernanceSummary(domain)}
          </span>
        )}
      </span>

      {!expanded && (
        <span className="hidden shrink-0 grid-cols-3 divide-x divide-base-300 lg:grid">
          {facts.map((fact) => (
            <span key={fact.label} className="min-w-24 px-4 text-center">
              <span className="block text-sm font-semibold leading-tight">{fact.value}</span>
              <span className="mt-0.5 block text-[0.68rem] text-base-content/50">{fact.label}</span>
            </span>
          ))}
        </span>
      )}

      <span className="flex shrink-0 items-center gap-2">
        {isCurrent && (
          <span className="badge badge-primary badge-outline badge-sm hidden sm:inline-flex">In use</span>
        )}
        <span className={`badge badge-sm hidden sm:inline-flex ${domain.active === false ? 'badge-ghost' : 'badge-success badge-outline'}`}>
          {domain.active === false ? 'Inactive' : 'Active'}
        </span>
      </span>
    </div>
  );
}

function DomainsBuilder() {
  const phaseView = usePhaseConfigurationView();
  const { schoolId } = useSchool();
  const { user } = useAuth();
  const { currentRole, loading: roleLoading } = useRoleSwitcher();
  const { domains, activeDomain, isLoaded, registryError } = useDomainStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rememberedDomainId, setRememberedDomainId] = useState(null);
  const [createdDomain, setCreatedDomain] = useState(null);
  const [collapsedDomainId, setCollapsedDomainId] = useState(null);
  const cardRefs = useRef(new Map());
  const lastScrolledDomainId = useRef(null);

  const rawTab = searchParams.get('step');
  const canonicalTab = LEGACY_TABS[rawTab] || rawTab;
  const activeTab = TABS.some((tab) => tab.key === canonicalTab) ? canonicalTab : 'domain';
  const showCreate = searchParams.get('create') === '1';
  const awardEditorRequest = searchParams.get('editor') === 'award'
    ? searchParams.get('award')
    : null;
  const domainEntries = useMemo(
    () => orderedDomainEntries([
      ...domains,
      ...(createdDomain && !domains.some((domain) => domain.id === createdDomain.id) ? [createdDomain] : []),
    ]),
    [createdDomain, domains],
  );
  const requestedParent = domainEntries.find(({ domain }) => (
    domain.id === searchParams.get('parent')
  ))?.domain || null;
  const selected = domainEntries.find(({ domain }) => domain.id === searchParams.get('domain'))?.domain
    || domainEntries.find(({ domain }) => domain.id === rememberedDomainId)?.domain
    || domainEntries[0]?.domain
    || null;

  useEffect(() => {
    if (!schoolId) return;
    try {
      setRememberedDomainId(sessionStorage.getItem(REMEMBERED_DOMAIN_KEY_PREFIX + schoolId));
    } catch {
      setRememberedDomainId(null);
    }
  }, [schoolId]);

  useEffect(() => {
    if (!isLoaded || showCreate || !selected) return;
    if (searchParams.get('domain') === selected.id && rawTab === activeTab) return;
    setSearchParams(buildSearch(searchParams, {
      domain: selected.id,
      step: activeTab,
      create: null,
    }), { replace: true });
  }, [activeTab, isLoaded, rawTab, searchParams, selected, setSearchParams, showCreate]);

  useEffect(() => {
    if (showCreate || !selected || lastScrolledDomainId.current === selected.id) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const card = cardRefs.current.get(selected.id);
      if (!card?.scrollIntoView) return;
      lastScrolledDomainId.current = selected.id;
      const bounds = card.getBoundingClientRect?.();
      // Keep the page introduction intact when the selected card is already
      // visible. Unconditionally aligning a large expanded card scrolled the
      // heading halfway behind Swan's fixed navigation on narrow screens.
      const visibleTop = 96;
      // A header peeking above the fold is not a useful deep-link result: keep
      // enough of the expanded editor visible to orient the administrator.
      // `lastScrolledDomainId` makes this a selected/open transition only, so
      // moving between tabs in the same Domain never causes another jump.
      const usefulTopLimit = Math.max(visibleTop + 240, window.innerHeight * 0.55);
      if (!bounds || bounds.top < visibleTop || bounds.top > usefulTopLimit) {
        card.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [selected, showCreate]);

  if (!isLoaded || roleLoading) {
    if (registryError && !roleLoading) return <DomainRegistryNotice />;
    return <div className="flex min-h-64 items-center justify-center"><LoadingSpinner /></div>;
  }
  if (!['admin', 'dev'].includes(currentRole)) {
    return (
      <div className="mx-auto max-w-xl p-6">
        <div className="alert alert-error">
          <LockClosedIcon className="h-5 w-5" />
          <span>Domain configuration is available to school administrators only.</span>
        </div>
      </div>
    );
  }

  const navigateTo = (tab, domainId = selected?.id) => {
    setSearchParams(buildSearch(searchParams, {
      domain: domainId || null,
      step: tab,
      create: null,
      parent: null,
      editor: null,
      award: null,
    }));
  };
  const closeAwardEditor = () => {
    setSearchParams(buildSearch(searchParams, {
      editor: null,
      award: null,
    }), { replace: true });
  };
  const chooseDomain = (domainId) => {
    setRememberedDomainId(domainId);
    try {
      sessionStorage.setItem(REMEMBERED_DOMAIN_KEY_PREFIX + schoolId, domainId);
    } catch {
      // The URL remains authoritative when storage is unavailable.
    }
    navigateTo(activeTab, domainId);
  };

  const renderTab = (domain) => {
    if (activeTab === 'domain') {
      return (
        <DomainSettingsCard
          embedded
          domain={domain}
          schoolId={schoolId}
          onCreated={(created) => {
            setCreatedDomain(created || null);
            if (created?.id) chooseDomain(created.id);
            navigateTo('event', created?.id || domain.id);
          }}
        />
      );
    }
    if (activeTab === 'event') {
      return <EventTab domain={domain} onNext={() => navigateTo('action', domain.id)} />;
    }
    if (activeTab === 'action') {
      return (
        <ActionTab
          domain={domain}
          schoolId={schoolId}
          onPrevious={() => navigateTo('event', domain.id)}
          onNext={() => navigateTo('rules', domain.id)}
          awardEditorRequest={awardEditorRequest}
          onAwardEditorClose={closeAwardEditor}
        />
      );
    }
    if (activeTab === 'rules') {
      return (
        <RulesTab
          domain={domain}
          schoolId={schoolId}
          onRequestAction={() => navigateTo('action', domain.id)}
        />
      );
    }
    return (
      <ReportsTab
        domain={domain}
        schoolId={schoolId}
        onPrevious={() => navigateTo('rules', domain.id)}
      />
    );
  };

  return (
    <main className="mx-auto min-w-0 w-[calc(100vw-2rem)] max-w-7xl space-y-5 overflow-x-hidden p-4 pb-24 sm:w-full sm:p-6 lg:p-8">
      <DomainRegistryNotice />
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Domains</h1>
          <p className="mt-2 max-w-2xl text-sm text-base-content/60">
            Define what your school records, what happens next, and how progress is understood.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PhaseScopeSelector align="start" />
          <button
            type="button"
            className="btn btn-primary gap-2 self-start shadow-sm sm:self-auto"
            onClick={() => setSearchParams(buildSearch(searchParams, { create: 1, parent: null }))}
            disabled={!schoolId || !user?.uid}
          >
            <PlusIcon className="h-4 w-4" /> New Domain
          </button>
        </div>
      </header>

      {!phaseView.ready && <p role="status" className="text-sm text-base-content/65">Waiting for current phase settings…</p>}
      {phaseView.ready && selected && !phaseView.matches(selected) &&
        <p role="status" className="text-sm text-base-content/65">Your open Domain is outside this phase view. Choose All phases to continue editing it.</p>}
      {phaseView.ready && domainEntries.length > 0 && !domainEntries.some(({ domain }) => phaseView.matches(domain)) &&
        <p className="text-sm text-base-content/65">No Domains in this phase view.</p>}

      {domainEntries.length ? (
        <section className="min-w-0 max-w-full space-y-2" aria-label="School Domains">
          {domainEntries.map(({ domain, depth }) => {
            const selectedDomain = domain.id === selected?.id;
            const expanded = selectedDomain && collapsedDomainId !== domain.id;
            return (
              <div
                key={domain.id}
                hidden={!phaseView.matches(domain)}
                ref={(node) => {
                  if (node) cardRefs.current.set(domain.id, node);
                  else cardRefs.current.delete(domain.id);
                }}
                className={`min-w-0 max-w-full scroll-mt-44 md:scroll-mt-28 ${depth > 0 ? 'ml-4 w-[calc(100%-1rem)] sm:ml-8 sm:w-[calc(100%-2rem)]' : ''}`}
              >
              <ExpandableCard
                flat
                expanded={expanded}
                onToggle={() => {
                  if (expanded) {
                    setCollapsedDomainId(domain.id);
                    return;
                  }
                  setCollapsedDomainId(null);
                  if (!selectedDomain) chooseDomain(domain.id);
                }}
                headerAriaLabel={`${expanded ? 'Collapse' : 'Expand'} ${displayLabel(domain)} Domain${domain.active === false ? ', inactive' : ''}`}
                className={`min-w-0 max-w-full overflow-hidden rounded-2xl [&>.card-body]:min-w-0 [&>.card-body]:px-4 [&>.card-body]:py-3.5 sm:[&>.card-body]:px-5 ${expanded ? 'border-primary/40 shadow-sm' : ''}`}
                header={(
                  <DomainCardHeader
                    domain={domain}
                    isCurrent={activeDomain?.id === domain.id}
                    expanded={expanded}
                  />
                )}
              >
                <DomainTabs
                  activeTab={activeTab}
                  domain={domain}
                  onSelect={(tab) => navigateTo(tab, domain.id)}
                />
                <div className="min-w-0 max-w-full overflow-hidden p-3 sm:p-6">
                  {renderTab(domain)}
                  <DomainJourneyNavigation
                    activeTab={activeTab}
                    domain={domain}
                    onSelect={(tab) => navigateTo(tab, domain.id)}
                  />
                </div>
              </ExpandableCard>
              </div>
            );
          })}
        </section>
      ) : (
        <EmptyState title="No Domains yet">Create the first Domain to decide what staff will record.</EmptyState>
      )}
      {showCreate && (
        <DomainCreationForm
          schoolId={schoolId}
          userId={user?.uid}
          parentDomainId={requestedParent?.id}
          parentDomainLabel={displayLabel(requestedParent)}
          onSuccess={(created) => {
            setCreatedDomain(created || null);
            const id = created?.id;
            if (id) {
              setRememberedDomainId(id);
              try {
                sessionStorage.setItem(REMEMBERED_DOMAIN_KEY_PREFIX + schoolId, id);
              } catch {
                // The URL remains authoritative when storage is unavailable.
              }
            }
            navigateTo('event', id || selected?.id);
          }}
          onCancel={() => navigateTo(activeTab)}
        />
      )}
    </main>
  );
}

export default function DomainsPanel({ mode = 'panel' }) {
  return mode === 'page' ? <DomainsBuilder /> : <DomainsQuickPanel />;
}
